/**
 * Project a recipient's REAL companion guests into DataTable row records for
 * the send / test-mail HTML preview — the client-side twin of the consumer's
 * `context-enricher.enrichCompanions` (main-consumer). At real send the
 * consumer joins the recipient's related guests and emits one row per
 * companion keyed by the catalog `key`; the marker expander then renders them.
 * The PSendEmail preview has no payload, so we fetch the companions client-side
 * (`getCompanions`) and run the SAME projection here, so the admin previews the
 * actual rows the recipient will receive instead of illustrative SAMPLE rows.
 *
 * The row shape contains exactly the keys declared in COMPANION_COLUMNS plus
 * flat `additionalFields.<name>` entries — no spread-everything shortcut, which
 * would expose `username`/`code` (login credentials), exactly like the consumer.
 *
 * KEEP IN SYNC with main-consumer/src/lib/context-enricher.ts (enrichCompanions)
 * and main-consumer/src/lib/humanize.ts (the label tables ported below).
 */
import { Guest } from "@/types/Guest";
import type { Field } from "@/types/Field";
import { COMPANION_COLUMNS, ADDITIONAL_FIELD_PREFIX } from "./data-table-catalog";
import {
  formatAdditionalFieldValue,
  localizedBoolean,
} from "@/lib/utils/additional-field-format";

/**
 * Localized companion participation labels [ACCEPTED, DECLINED, PENDING], keyed
 * by primary language subtag. English is the fallback. KEEP IN SYNC with
 * main-consumer/src/lib/humanize.ts ACCEPTANCE_STATE_LABELS.
 */
const ACCEPTANCE_STATE_LABELS: Record<string, readonly [string, string, string]> = {
  bg: ["Прието", "Отхвърлено", "В изчакване"],
  ca: ["Acceptat", "Rebutjat", "Pendent"],
  cs: ["Přijato", "Zamítnuto", "Čeká se"],
  cy: ["Wedi derbyn", "Wedi gwrthod", "Yn yr arfaeth"],
  da: ["Accepteret", "Afvist", "Afventer"],
  de: ["Akzeptiert", "Abgelehnt", "Ausstehend"],
  el: ["Αποδεκτό", "Απορρίφθηκε", "Σε εκκρεμότητα"],
  en: ["Accepted", "Declined", "Pending"],
  es: ["Aceptado", "Rechazado", "Pendiente"],
  et: ["Vastu võetud", "Tagasi lükatud", "Ootel"],
  eu: ["Onartua", "Baztertua", "Zain"],
  fi: ["Hyväksytty", "Kieltäytynyt", "Odottaa"],
  fr: ["Accepté", "Refusé", "En attente"],
  ga: ["Glactha", "Diúltaithe", "Ar Feitheamh"],
  gd: ["Air gabhail ris", "Air a dhiùltadh", "Ri thighinn"],
  gl: ["Aceptado", "Rexeitado", "Pendente"],
  hr: ["Prihvaćeno", "Odbijeno", "Na čekanju"],
  hu: ["Elfogadva", "Elutasítva", "Függőben"],
  is: ["Samþykkt", "Hafnað", "Í bið"],
  it: ["Accettato", "Rifiutato", "In attesa"],
  lb: ["Akzeptéiert", "Ofgeleent", "Ausstoend"],
  lt: ["Priimta", "Atmesta", "Laukiama"],
  lv: ["Pieņemts", "Noraidīts", "Gaida"],
  mt: ["Aċċettat", "Miċħud", "Pendenti"],
  nb: ["Akseptert", "Avslått", "Venter"],
  nl: ["Geaccepteerd", "Afgewezen", "In afwachting"],
  pl: ["Zaakceptowany", "Odrzucony", "Oczekujący"],
  pt: ["Aceite", "Recusado", "Pendente"],
  rm: ["Acceptà", "Refusà", "En spetga"],
  ro: ["Acceptat", "Respins", "În așteptare"],
  sk: ["Akceptované", "Odmietnuté", "Čakajúce"],
  sl: ["Sprejeto", "Zavrnjeno", "V čakanju"],
  sq: ["Pranuar", "Refuzuar", "Në pritje"],
  sr: ["Prihvaćeno", "Odbijeno", "Na čekanju"],
  sv: ["Accepterad", "Avböjd", "Väntar"],
};

function primaryLang(lang: string | null | undefined): string {
  return typeof lang === "string" && lang.length > 0
    ? lang.split("-")[0]!.toLowerCase()
    : "en";
}

export function humanizeAcceptanceState(
  value: unknown,
  lang: string | null | undefined,
): string {
  if (typeof value !== "string" || value.length === 0) return "";
  const labels = ACCEPTANCE_STATE_LABELS[primaryLang(lang)] ?? ACCEPTANCE_STATE_LABELS["en"]!;
  switch (value) {
    case "ACCEPTED":
    case "SYSTEM":
      return labels[0];
    case "DECLINED":
      return labels[1];
    case "PENDING":
      return labels[2];
    default:
      return "";
  }
}

/**
 * Render a single field value by runtime type — booleans become localized
 * Yes/No, everything else passes through as a string. Mirrors the consumer's
 * `formatFieldValue` for the value shapes that survive a JSON API round-trip
 * (dates arrive as ISO strings client-side, so they pass through unchanged —
 * the same illustrative limitation the SAMPLE rows already carried).
 */
function formatValue(value: unknown, lang: string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "boolean") return localizedBoolean(value, lang);
  return String(value);
}

/**
 * Project the recipient's real companions into preview row records keyed by the
 * catalog `key`. `lang` is the RECIPIENT's language (the consumer localizes all
 * companion rows in the recipient's language, not each companion's own).
 */
export function buildCompanionRecords(
  companions: Guest[],
  lang: string | null | undefined,
  /** Additional-field definitions keyed by field NAME — drives type-aware
   *  rendering (boolean / date / currency / double) via formatAdditionalFieldValue. */
  fieldsByName?: ReadonlyMap<string, Field>,
  /** Event timezone for DATE_TIME columns (matches the real send). */
  timeZone?: string | null,
): Record<string, string>[] {
  return companions.map((c) => {
    const src = c as unknown as Record<string, unknown>;
    const row: Record<string, string> = {};
    for (const col of COMPANION_COLUMNS) {
      if (col.key === "acceptanceState") {
        // Keep RAW (ACCEPTED/SYSTEM/PENDING/DECLINED) so the marker expander can
        // apply the participation filter, then humanize after — mirrors how
        // appointment-record keeps the raw appointmentStatus for its filter.
        row[col.key] =
          typeof src["acceptanceState"] === "string"
            ? (src["acceptanceState"] as string)
            : "";
      } else {
        row[col.key] = formatValue(src[col.key], lang);
      }
    }
    const af = c.additionalFields;
    if (af && typeof af === "object" && !Array.isArray(af)) {
      for (const [k, v] of Object.entries(af)) {
        // Type-aware render by the field's declared dataType (CURRENCY → money,
        // DOUBLE → grouped decimal, DATE/DATE_TIME → localized date[+time],
        // BOOLEAN → Yes/No) — matching the consumer email render.
        row[`${ADDITIONAL_FIELD_PREFIX}${k}`] = formatAdditionalFieldValue(
          v,
          fieldsByName?.get(k),
          lang,
          timeZone,
        );
      }
    }
    return row;
  });
}

/** Fields projection requested from `getCompanions` so every companion column
 *  (and additional fields) has data to render. */
export const COMPANION_PREVIEW_FIELDS = [
  "guestId",
  "guestType",
  "additionalFields",
  ...COMPANION_COLUMNS.map((c) => c.key),
].join(",");
